import * as amplitude from "@amplitude/unified";
import {
  getAmplitudeSessionReplayProxyConfig,
} from "@/lib/analytics/amplitude-session-replay-proxy";

import type {
  BaseEventProps,
  EventName,
  EventPropsMap,
  FlowEventName,
  ResultEventName,
  UiEventName,
} from "@/lib/analytics/types";

const AMPLITUDE_ENV =
  process.env.NEXT_PUBLIC_AMPLITUDE_ENV ??
  (process.env.NODE_ENV === "production" ? "prod" : "dev");
const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
const AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE = Number.parseFloat(
  process.env.NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE ?? "1"
);
const AMPLITUDE_SESSION_REPLAY_ENABLED =
  process.env.NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY_ENABLED === "true";

const BUILD_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? "unknown";
const SESSION_STORAGE_KEY = "aseed-amplitude-session-id";
const DEVICE_STORAGE_KEY = "aseed-amplitude-device-id";
let inMemorySessionId: string | null = null;
let inMemoryDeviceId: string | null = null;
let runtimeAmplitudeEnv: "dev" | "prod" | null = null;
type AmplitudeInitMode = "analytics" | "analytics+replay";

type AseedGlobal = typeof globalThis & {
  __aseedAmplitudeInitialized?: boolean;
  __aseedAmplitudeClient?: ReturnType<typeof amplitude.createInstance>;
  __aseedAmplitudeInitMode?: AmplitudeInitMode;
};

function isClientSide(): boolean {
  return typeof window !== "undefined";
}

function createSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const randomPart = Math.random().toString(36).slice(2, 10);
  return `session_${Date.now()}_${randomPart}`;
}

function resolveRuntimeAmplitudeEnv(): "dev" | "prod" {
  if (runtimeAmplitudeEnv) {
    return runtimeAmplitudeEnv;
  }

  if (isClientSide()) {
    const hostname = window.location.hostname.toLowerCase();
    const isLocalHost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.endsWith(".local");
    const isDevHost = hostname.startsWith("dev.") || hostname.includes("-dev.");
    const isProdHost =
      hostname === "app.aseed.ai" || hostname.endsWith(".aseed.ai");

    if (isProdHost && !isLocalHost && !isDevHost) {
      runtimeAmplitudeEnv = "prod";
      return runtimeAmplitudeEnv;
    }

    if (isLocalHost || isDevHost) {
      runtimeAmplitudeEnv = "dev";
      return runtimeAmplitudeEnv;
    }
  }

  runtimeAmplitudeEnv = AMPLITUDE_ENV === "prod" ? "prod" : "dev";
  return runtimeAmplitudeEnv;
}

function getAmplitudeClient(): ReturnType<typeof amplitude.createInstance> {
  const amplitudeGlobal = globalThis as AseedGlobal;
  if (!amplitudeGlobal.__aseedAmplitudeClient) {
    amplitudeGlobal.__aseedAmplitudeClient = amplitude.createInstance();
  }

  return amplitudeGlobal.__aseedAmplitudeClient;
}

function getAmplitudeDeviceId(): string | null {
  if (!isClientSide()) {
    return null;
  }

  if (inMemoryDeviceId) {
    return inMemoryDeviceId;
  }

  try {
    const existing = window.localStorage.getItem(DEVICE_STORAGE_KEY);
    if (existing) {
      inMemoryDeviceId = existing;
      return existing;
    }
  } catch {
    // Ignore storage access errors in privacy-restricted environments.
  }

  const newDeviceId = createSessionId();
  inMemoryDeviceId = newDeviceId;
  try {
    window.localStorage.setItem(DEVICE_STORAGE_KEY, newDeviceId);
  } catch {
    // Ignore storage write errors and keep in-memory fallback.
  }
  return newDeviceId;
}

function isAuthRoute(): boolean {
  if (!isClientSide()) {
    return false;
  }

  const pathname = window.location.pathname.toLowerCase();
  return (
    pathname === "/login" ||
    pathname.startsWith("/login/") ||
    pathname === "/signup" ||
    pathname.startsWith("/signup/") ||
    pathname === "/forgot-password" ||
    pathname.startsWith("/forgot-password/") ||
    pathname === "/reset-password" ||
    pathname.startsWith("/reset-password/")
  );
}

function shouldEnableSessionReplay(): boolean {
  if (!AMPLITUDE_SESSION_REPLAY_ENABLED) {
    return false;
  }

  if (!Number.isFinite(AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE)) {
    return false;
  }

  if (AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE <= 0) {
    return false;
  }

  return !isAuthRoute();
}

export function initializeAmplitude(): boolean {
  if (!isClientSide()) {
    return false;
  }

  const amplitudeGlobal = globalThis as AseedGlobal;
  const shouldInitReplay = shouldEnableSessionReplay();
  const currentMode = amplitudeGlobal.__aseedAmplitudeInitMode;

  if (
    amplitudeGlobal.__aseedAmplitudeInitialized &&
    (currentMode === "analytics+replay" || !shouldInitReplay)
  ) {
    return true;
  }

  if (typeof AMPLITUDE_API_KEY !== "string" || AMPLITUDE_API_KEY.length === 0) {
    return false;
  }

  const amplitudeClient = getAmplitudeClient();
  const amplitudeDeviceId = getAmplitudeDeviceId();
  const analyticsOptions = {
    autocapture: true,
    fetchRemoteConfig: false,
    defaultTracking: true,
    ...(amplitudeDeviceId ? { deviceId: amplitudeDeviceId } : {}),
  };

  if (shouldInitReplay) {
    const sessionReplayProxyConfig = getAmplitudeSessionReplayProxyConfig();
    amplitudeClient.initAll(AMPLITUDE_API_KEY, {
      analytics: analyticsOptions,
      sessionReplay: {
        sampleRate: AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE,
        ...sessionReplayProxyConfig,
      },
    });
    amplitudeGlobal.__aseedAmplitudeInitMode = "analytics+replay";
  } else {
    amplitudeClient.init(AMPLITUDE_API_KEY, undefined, analyticsOptions);
    amplitudeGlobal.__aseedAmplitudeInitMode = "analytics";
  }

  amplitudeGlobal.__aseedAmplitudeInitialized = true;
  return true;
}

function getSessionId(): string {
  if (!isClientSide()) {
    return "server";
  }

  if (inMemorySessionId) {
    return inMemorySessionId;
  }

  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      inMemorySessionId = existing;
      return existing;
    }
  } catch {
    // Ignore storage access errors in privacy-restricted environments.
  }

  const newSessionId = createSessionId();
  inMemorySessionId = newSessionId;
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
  } catch {
    // Ignore storage write errors and keep in-memory fallback.
  }
  return newSessionId;
}

function getCurrentRoute(): string {
  if (!isClientSide()) {
    return "/";
  }

  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const longTokenPattern = /^[A-Za-z0-9_-]{20,}$/;
  const pathname = window.location.pathname || "/";

  const normalizedRoute = pathname
    .split("/")
    .map((segment) => {
      if (!segment || segment === "public") {
        return segment;
      }

      if (uuidPattern.test(segment) || longTokenPattern.test(segment)) {
        return ":id";
      }

      return segment;
    })
    .join("/");

  return normalizedRoute || "/";
}

function getBaseEventProperties(): BaseEventProps {
  const env = resolveRuntimeAmplitudeEnv();

  return {
    env,
    environment: env,
    app: "aseed-client",
    platform: "web",
    source: "frontend",
    source_service: "aseed-client",
    route: getCurrentRoute(),
    build_version: BUILD_VERSION,
    session_id: getSessionId(),
  };
}

export function trackAmplitudeEvent<T extends EventName>(
  eventType: T,
  eventProperties: EventPropsMap[T] = {} as EventPropsMap[T]
): void {
  if (!isClientSide()) {
    return;
  }

  if (!initializeAmplitude()) {
    return;
  }

  getAmplitudeClient().track(eventType, {
    ...getBaseEventProperties(),
    ...eventProperties,
  });
}

export function trackUi<T extends UiEventName>(
  eventType: T,
  eventProperties: EventPropsMap[T] = {} as EventPropsMap[T]
): void {
  trackAmplitudeEvent(eventType, eventProperties);
}

export function trackFlow<T extends FlowEventName>(
  eventType: T,
  eventProperties: EventPropsMap[T] = {} as EventPropsMap[T]
): void {
  trackAmplitudeEvent(eventType, eventProperties);
}

export function trackResult<T extends ResultEventName>(
  eventType: T,
  eventProperties: EventPropsMap[T] = {} as EventPropsMap[T]
): void {
  trackAmplitudeEvent(eventType, eventProperties);
}

export function identifyUser(userId: string | null | undefined): void {
  if (!initializeAmplitude()) {
    return;
  }

  getAmplitudeClient().setUserId(userId ?? undefined);
}

export function resetUser(): void {
  if (!initializeAmplitude()) {
    return;
  }

  getAmplitudeClient().reset();
}

export function getAmplitudeDeviceIdForHeader(): string | null {
  return getAmplitudeDeviceId();
}

// Backward-compatible aliases for existing callers.
export const setAmplitudeUserId = identifyUser;
export const resetAmplitudeIdentity = resetUser;
